import React, { useState } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Alert,
  Paper,
  Snackbar,
  CircularProgress,
  Chip,
  Typography,
} from "@mui/material";
import {
  Inventory as InventoryIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const OfficeInventoryForm = () => {
  const [formData, setFormData] = useState({
    boxID: "",
    serialNumber: "",
    itemName: "",
    category: "",
    brandModel: "",
    supplier: "",
    purchaseDate: "",
    warrantyExpiryDate: "",
    price: "",
    stock: 1,
    notes: "",
    status: "In Stock",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess("");

    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      const response = await fetch(
        `${BASE_URL}/api/inventory/office-inventory`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      // First check if the response is JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Server returned ${response.status}: ${text}`);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Error creating inventory item");
      }

      setSuccess("Inventory item created successfully!");
      setFormData({
        boxID: "",
        serialNumber: "",
        itemName: "",
        category: "",
        brandModel: "",
        supplier: "",
        purchaseDate: "",
        warrantyExpiryDate: "",
        price: "",
        stock: 1,
        notes: "",
        status: "In Stock",
      });
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.message ||
          "Error creating inventory item. Please check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "0.875rem",
      "& fieldset": { borderColor: "#eef0f6" },
      "&:hover fieldset": { borderColor: "#CBD5E1" },
      "&.Mui-focused fieldset": { borderColor: "#667eea" },
    },
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Add Inventory Item"
        subtitle="Add new inventory items to your office system"
        icon={<InventoryIcon />}
      />

      {/* Success/Error Snackbar */}
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess("")} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSuccess("")} severity="success" variant="filled" sx={{ borderRadius: "8px" }}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ borderRadius: "8px" }}>{error}</Alert>
      </Snackbar>

      <Paper elevation={0} sx={{ border: "1px solid #eef0f6", borderRadius: "16px", p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Identification Section */}
          <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1a1a2e", mb: 2 }}>
            Identification
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Box ID" name="boxID" value={formData.boxID} onChange={handleChange} placeholder="e.g., BX-001" sx={inputSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Serial Number" name="serialNumber" value={formData.serialNumber} onChange={handleChange} placeholder="Enter serial number" sx={inputSx} />
            </Grid>
          </Grid>

          {/* Item Details Section */}
          <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1a1a2e", mb: 2 }}>
            Item Details
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Item Name" name="itemName" value={formData.itemName} onChange={handleChange} placeholder="Enter item name" sx={inputSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Category" name="category" value={formData.category} onChange={handleChange} placeholder="Enter category" sx={inputSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Brand/Model" name="brandModel" value={formData.brandModel} onChange={handleChange} placeholder="Enter brand/model" sx={inputSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Supplier" name="supplier" value={formData.supplier} onChange={handleChange} placeholder="Enter supplier name" sx={inputSx} />
            </Grid>
          </Grid>

          {/* Financial & Dates Section */}
          <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1a1a2e", mb: 2 }}>
            Financial & Dates
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Purchase Date" name="purchaseDate" type="date" value={formData.purchaseDate} onChange={handleChange}
                InputLabelProps={{ shrink: true }} sx={inputSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Warranty Expiry Date" name="warrantyExpiryDate" type="date" value={formData.warrantyExpiryDate} onChange={handleChange}
                InputLabelProps={{ shrink: true }} inputProps={{ min: formData.purchaseDate }} sx={inputSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Price" name="price" type="number" value={formData.price} onChange={handleChange} placeholder="0.00"
                inputProps={{ min: 0, step: 0.01 }} sx={inputSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Stock Quantity" name="stock" type="number" value={formData.stock} onChange={handleChange} placeholder="1"
                inputProps={{ min: 1 }} sx={inputSx} />
            </Grid>
          </Grid>

          {/* Status & Notes Section */}
          <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1a1a2e", mb: 2 }}>
            Status & Notes
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select name="status" value={formData.status} onChange={handleChange} label="Status"
                  sx={{ borderRadius: "8px", fontSize: "0.875rem", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#eef0f6" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#667eea" } }}>
                  <MenuItem value="In Stock">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label="In Stock" size="small" sx={{ bgcolor: "#D1FAE5", color: "#065F46", fontWeight: 600, fontSize: "0.75rem", height: 22 }} />
                    </Box>
                  </MenuItem>
                  <MenuItem value="Assigned">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label="Assigned" size="small" sx={{ bgcolor: "#eef0ff", color: "#667eea", fontWeight: 600, fontSize: "0.75rem", height: 22 }} />
                    </Box>
                  </MenuItem>
                  <MenuItem value="Maintenance">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label="Maintenance" size="small" sx={{ bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 600, fontSize: "0.75rem", height: 22 }} />
                    </Box>
                  </MenuItem>
                  <MenuItem value="Retired">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label="Retired" size="small" sx={{ bgcolor: "#FEE2E2", color: "#991B1B", fontWeight: 600, fontSize: "0.75rem", height: 22 }} />
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Notes" name="notes" value={formData.notes} onChange={handleChange} placeholder="Additional notes about the item"
                multiline rows={2} sx={inputSx} />
            </Grid>
          </Grid>

          {/* Submit Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2, borderTop: "1px solid #f0f2f8" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                py: 1,
                boxShadow: "none",
                "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)", boxShadow: "none" },
                "&:disabled": { bgcolor: "#eef0f6", color: "#8b8fa3" },
              }}
            >
              {loading ? "Creating..." : "Create Inventory Item"}
            </Button>
          </Box>
        </form>
      </Paper>
    </PageContainer>
  );
};

export default OfficeInventoryForm;
